import { Injectable, Optional } from '@angular/core';
import {
  DefaultUrlSerializer,
  NavigationEnd,
  Router,
  UrlSerializer,
} from '@angular/router';
import { WindowRefService } from '@callrail/looky/util';
import { combineLatest } from 'rxjs';

import { AgencyModel } from '../../models/agency.model';
import { CompanyModel } from '../../models/company.model';
import { UserModel } from '../../models/user.model';
import { CurrentContextService } from './../current-context/current-context.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsReporterService {
  private tracking: boolean = false;
  private gAnalytics = null;
  private urlSerializer: UrlSerializer;
  private agency: AgencyModel;
  private company: CompanyModel;
  private user: UserModel;

  // We track custom dimensions for these, and so don't want them in the reported URL
  private dimensionParams: string[] = ['agency_id', 'company_id'];

  constructor(
    @Optional() private router: Router,
    private currentContextService: CurrentContextService,
    private windowRef: WindowRefService
  ) {
    this.urlSerializer = new DefaultUrlSerializer();

    combineLatest([
      currentContextService.agency,
      currentContextService.company,
      currentContextService.user,
    ]).subscribe(([agency, company, user]) => {
      this.agency = agency;
      this.company = company;
      this.user = user;
    });
  }

  public trackPageRouting(baseUrl) {
    if (!this.tracking && this.router) {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.setUserId();
          this.setGlobalDimensions();

          const tree = this.urlSerializer.parse(event.url);
          this.dimensionParams.forEach((key) => delete tree.queryParams[key]);

          this.setPageView(`/${baseUrl}${tree.toString()}`);
        }
      });
      this.tracking = true;
    }
  }

  public setPageView(url: string, customFields?: { [k: string]: string }) {
    this.ga('set', 'page', url);
    this.ga('send', 'pageview', { ...customFields });
  }

  public setUserId() {
    if (this.user) {
      // Set the user ID using signed-in user_id.
      this.ga('set', '&uid', this.user.id);
    }
  }

  // Only use this method of setting a custom dimension when you want the dimension sent
  // with all the hits on a given page (or for the lifetime of the tracker object).
  public setCustomDimension(dimension: { id: string; data: string }): void {
    const { id, data } = dimension;
    this.ga('set', id, data);
  }

  public emitEvent(
    eventCategory: string,
    eventAction: string,
    eventLabel: string = null,
    eventValue: number = null,
    eventFields?: { [k: string]: string }
  ): void {
    this.ga('send', 'event', {
      eventCategory,
      eventLabel,
      eventAction,
      eventValue,
      ...eventFields,
    });
  }

  private setGlobalDimensions(): void {
    const agencyId = this.agency ? this.agency.id.toString() : '';
    const companyId = this.company ? this.company.id.toString() : '';
    const paidStatus = this.user.paid_user ? 'paid' : 'trial';

    this.setCustomDimension({ id: 'dimension1', data: agencyId });
    this.setCustomDimension({ id: 'dimension2', data: companyId });
    this.setCustomDimension({ id: 'dimension3', data: this.user.role });
    this.setCustomDimension({ id: 'dimension4', data: paidStatus });
    this.setCustomDimension({
      id: 'dimension5',
      data: this.gaProductSetDisplay(),
    });
    this.setCustomDimension({
      id: 'dimension7',
      data: `${this.user.active_agencies_count}`,
    });
  }

  private analyticsTrackerExists(): boolean {
    if (this.gAnalytics) {
      return true;
    }

    const ga = this.windowRef.nativeWindow().ga;
    this.gAnalytics = ga ? ga : null;
    return !!ga;
  }

  private ga(command, ...args): void {
    if (this.analyticsTrackerExists()) {
      this.gAnalytics(command, ...args);
    }
  }

  private gaProductSetDisplay(): string {
    const products = [];
    const agency = this.agency;
    if (agency?.calls_enabled) {
      products.push('Calls');
    }
    if (agency?.hasFeature('lc_chat')) {
      products.push('Chats');
    }
    if (agency?.forms_enabled) {
      products.push('Forms');
    }
    return products.join(', ');
  }
}
